import { useEffect, useState } from 'react';

import {
  COMFORTABLE_DESKTOP_MIN_WIDTH,
  DESKTOP_MIN_WIDTH,
  TABLET_MIN_WIDTH,
  getIsDesktop,
  getIsMobileLayout,
  getIsPhone,
  getIsViewportDesktop,
  getLayoutWidth,
  getViewportWidth,
  getWindowWidth,
} from '@/utils/infrastructure/viewportWidth';

export {
  COMFORTABLE_DESKTOP_MIN_WIDTH,
  DESKTOP_MIN_WIDTH,
  TABLET_MIN_WIDTH,
  getIsDesktop,
  getIsMobileLayout,
  getIsPhone,
  getIsViewportDesktop,
  getLayoutWidth,
  getViewportWidth,
  getWindowWidth,
};

/**
 * Shared viewport width store for React subscribers.
 *
 * Prefer this (or useIsMobile / withResponsive) for UI. The webpack alias
 * `react-device-detect$` → deviceDetect.js covers legacy imports, but memoized
 * children still need a hook/HOC to re-render on band changes.
 */
let currentWidth = getWindowWidth();
const listeners = new Set();
let listening = false;
let rafId = 0;

const bandFor = (width) => {
  if (width < TABLET_MIN_WIDTH) return 'mobile';
  if (width < DESKTOP_MIN_WIDTH) return 'tablet';
  if (width < COMFORTABLE_DESKTOP_MIN_WIDTH) return 'narrow-desktop';
  return 'comfortable';
};

const publish = (nextWidth) => {
  if (nextWidth === currentWidth) return;

  const previousBand = bandFor(currentWidth);
  const nextBand = bandFor(nextWidth);
  currentWidth = nextWidth;

  listeners.forEach((listener) => listener(currentWidth));

  const wasDesktop = previousBand === 'narrow-desktop' || previousBand === 'comfortable';
  const isDesktopNow = nextBand === 'narrow-desktop' || nextBand === 'comfortable';
  if (wasDesktop && !isDesktopNow && previousBand !== nextBand) {
    import('@/utils/infrastructure')
      .then(({ hideAllOffCanvas }) => hideAllOffCanvas())
      .catch(() => {});
  }
};

const flushWidth = () => {
  rafId = 0;
  // Layout width accounts for docked chat (`viewport − --chat-dock-space`).
  publish(getLayoutWidth());
};

const scheduleFlush = () => {
  if (rafId) return;
  rafId = window.requestAnimationFrame(flushWidth);
};

/** Call after chat dock open/resize/close so shell breakpoints re-evaluate. */
export const notifyLayoutWidthChanged = () => {
  if (typeof window === 'undefined') return;
  ensureListening();
  scheduleFlush();
};

const ensureListening = () => {
  if (listening || typeof window === 'undefined') return;
  listening = true;

  window.addEventListener('resize', scheduleFlush);
  window.addEventListener('orientationchange', scheduleFlush);

  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', scheduleFlush);
  }

  const medias = [
    window.matchMedia(`(min-width: ${DESKTOP_MIN_WIDTH}px)`),
    window.matchMedia(`(min-width: ${COMFORTABLE_DESKTOP_MIN_WIDTH}px)`),
    window.matchMedia(`(max-width: ${TABLET_MIN_WIDTH - 1}px)`),
  ];

  medias.forEach((mq) => {
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', scheduleFlush);
    } else if (typeof mq.addListener === 'function') {
      mq.addListener(scheduleFlush);
    }
  });
};

const subscribe = (listener) => {
  ensureListening();
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const derive = (screenWidth) => {
  const shouldUseMobileLayout = screenWidth < DESKTOP_MIN_WIDTH;
  const shouldUseDesktopLayout = screenWidth >= DESKTOP_MIN_WIDTH;
  const isMobile = screenWidth < TABLET_MIN_WIDTH;
  const isTabletSize = screenWidth >= TABLET_MIN_WIDTH && screenWidth < DESKTOP_MIN_WIDTH;
  const isDesktopSize = screenWidth >= DESKTOP_MIN_WIDTH;
  const shouldCollapseSider = shouldUseDesktopLayout && screenWidth < COMFORTABLE_DESKTOP_MIN_WIDTH;

  return {
    screenWidth,
    isCustomDesktop: shouldUseDesktopLayout,
    isCustomTabletOrMobile: shouldUseMobileLayout,
    shouldUseMobileLayout,
    shouldUseDesktopLayout,
    shouldCollapseSider,
    isInitialized: typeof window !== 'undefined',
    forceUpdate: screenWidth,
    previousBreakpoint: bandFor(screenWidth),
    // Width bands
    isMobile, // < 768 phone
    isTabletSize, // 768–1179
    isDesktopSize, // ≥ 1180
    // Drop-in aliases (width-based)
    isMobileOnly: shouldUseMobileLayout, // < 1180 shell
    isTablet: isTabletSize,
    isDesktop: isDesktopSize,
  };
};

export const useResponsive = () => {
  const [screenWidth, setScreenWidth] = useState(currentWidth);

  useEffect(() => {
    setScreenWidth(currentWidth);
    publish(getLayoutWidth());
    return subscribe((next) => setScreenWidth(next));
  }, []);

  return derive(screenWidth);
};

/** Layout móvil / tablet shell (layout width &lt; 1180, chat dock subtracted). */
export const useIsMobile = () => useResponsive().shouldUseMobileLayout;

/** Banda teléfono (layout width &lt; 768). */
export const useIsPhone = () => useResponsive().isMobile;

/** Layout desktop (layout width ≥ 1180). */
export const useIsDesktop = () => useResponsive().shouldUseDesktopLayout;

/**
 * Raw viewport desktop (≥ 1180), ignores docked chat.
 * Use for chat docking eligibility to avoid dock ↔ undock oscillation.
 */
export const useIsViewportDesktop = () => {
  const [isDesktopViewport, setIsDesktopViewport] = useState(getIsViewportDesktop);

  useEffect(() => {
    const sync = () => setIsDesktopViewport(getIsViewportDesktop());
    sync();
    window.addEventListener('resize', sync);
    window.addEventListener('orientationchange', sync);
    return () => {
      window.removeEventListener('resize', sync);
      window.removeEventListener('orientationchange', sync);
    };
  }, []);

  return isDesktopViewport;
};

// Compat aliases for non-React call sites
export const getIsMobileOnly = getIsMobileLayout;
